import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { GenerateButton, Textarea, Label, Alert, AlertDescription, Checkbox } from '@creanizer/ui'
import { FileText, RefreshCw } from 'lucide-react'
import type { SocietyInput, SocietyResult, SavedWorldBuilding, SavedCharacter, SavedCommunity } from '@creanizer/types'
import { StandardModeForm } from './StandardModeForm'
import { DetailedModeForm } from './DetailedModeForm'
import { TemplateModeForm } from './TemplateModeForm'
import { IdeaModeForm } from './IdeaModeForm'
import { BatchModeForm } from './BatchModeForm'
import { ConnectionsForm } from './ConnectionsForm'
import { GenerationOptionsForm } from './GenerationOptionsForm'

interface SocietyFormProps {
  input: SocietyInput
  onChange: (input: SocietyInput) => void
  onGenerate: () => void
  isGenerating: boolean
  useStreaming: boolean
  onStreamingChange: (use: boolean) => void
  savedWorlds: SavedWorldBuilding[]
  savedCharacters: SavedCharacter[]
  savedCommunities: SavedCommunity[]
  // 更新モード用
  updateMode?: boolean
  updateSource?: string
  onUpdateSourceChange?: (source: string) => void
  onUpdateModeChange?: (mode: boolean) => void
  existingResult?: SocietyResult | null
}

export function SocietyForm({
  input,
  onChange,
  onGenerate,
  isGenerating,
  useStreaming,
  onStreamingChange,
  savedWorlds,
  savedCharacters,
  savedCommunities,
  updateMode = false,
  updateSource = '',
  onUpdateSourceChange,
  onUpdateModeChange,
  existingResult,
}: SocietyFormProps) {
  // モード変更時のハンドラ - 他のモードの入力をクリア
  const handleModeChange = (newMode: string) => {
    const baseFields = {
      societyType: input.societyType || 'guild',
      scale: input.scale || 'medium',
      genre: input.genre || 'fantasy',
      creativityLevel: input.creativityLevel || 7,
      useProMode: input.useProMode,
    }

    if (newMode === 'standard') {
      onChange({
        ...baseFields,
        mode: 'standard',
        influence: input.influence || 5,
        secrecy: input.secrecy || 3,
        resources: input.resources || 'adequate',
      })
    } else if (newMode === 'detailed') {
      onChange({
        ...baseFields,
        mode: 'detailed',
        purpose: input.purpose || '',
        alignment: input.alignment,
        influence: input.influence || 5,
        secrecy: input.secrecy || 3,
        resources: input.resources || 'adequate',
        primaryActivity: input.primaryActivity || '',
        operationalArea: input.operationalArea || '',
        additionalSettings: input.additionalSettings || '',
      })
    } else if (newMode === 'template') {
      onChange({
        ...baseFields,
        mode: 'template',
        templateType: input.templateType || '',
        templateModifiers: input.templateModifiers || [],
      })
    } else if (newMode === 'idea') {
      onChange({
        mode: 'idea',
        ideaText: input.ideaText || '',
        creativityLevel: input.creativityLevel || 7,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'batch') {
      onChange({
        ...baseFields,
        mode: 'batch',
        numberOfSocieties: input.numberOfSocieties || 3,
        batchTheme: input.batchTheme || '',
      })
    }
  }

  return (
    <div className="space-y-6">
      <Tabs value={input.mode || 'standard'} onValueChange={handleModeChange}>
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 gap-1 h-auto">
          <TabsTrigger value="standard" className="text-xs sm:text-sm">標準</TabsTrigger>
          <TabsTrigger value="detailed" className="text-xs sm:text-sm">詳細</TabsTrigger>
          <TabsTrigger value="template" className="text-xs sm:text-sm">テンプレート</TabsTrigger>
          <TabsTrigger value="idea" className="text-xs sm:text-sm">アイディア</TabsTrigger>
          <TabsTrigger value="batch" className="text-xs sm:text-sm">バッチ</TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="space-y-4">
          <StandardModeForm input={input} onChange={onChange} />
        </TabsContent>

        <TabsContent value="detailed" className="space-y-4">
          <DetailedModeForm input={input} onChange={onChange} />
        </TabsContent>

        <TabsContent value="template" className="space-y-4">
          <TemplateModeForm input={input} onChange={onChange} />
        </TabsContent>

        <TabsContent value="idea" className="space-y-4">
          <IdeaModeForm input={input} onChange={onChange} />
        </TabsContent>

        <TabsContent value="batch" className="space-y-4">
          <BatchModeForm input={input} onChange={onChange} />
        </TabsContent>
      </Tabs>

      {/* 共通の関連設定（すべてのモードで表示） */}
      <ConnectionsForm
        input={input}
        onChange={onChange}
        savedWorlds={savedWorlds}
        savedCharacters={savedCharacters}
        savedCommunities={savedCommunities}
      />

      {/* 生成オプション */}
      <GenerationOptionsForm
        input={input}
        onChange={onChange}
        useStreaming={useStreaming}
        onStreamingChange={onStreamingChange}
      />

      {/* 更新モード */}
      {existingResult && onUpdateModeChange && (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="updateMode"
              checked={updateMode}
              onCheckedChange={(checked) => onUpdateModeChange(checked as boolean)}
            />
            <Label htmlFor="updateMode" className="font-normal cursor-pointer flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              情報更新モード
              <span className="text-xs text-muted-foreground">
                既存の組織設定に追加情報を反映します
              </span>
            </Label>
          </div>

          {updateMode && (
            <div className="space-y-3">
              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  更新対象: <strong>{existingResult.basicInfo.name}</strong>
                  <br />
                  <span className="text-xs text-muted-foreground">
                    下記の更新内容を元に、組織設定が更新されます。
                  </span>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="updateSource">更新内容（自由記述）</Label>
                <Textarea
                  id="updateSource"
                  value={updateSource}
                  onChange={(e) => onUpdateSourceChange?.(e.target.value)}
                  placeholder="例：新しいメンバーの加入、組織の活動拡大、敵対組織との抗争..."
                  className="min-h-[150px]"
                />
                <p className="text-xs text-muted-foreground">
                  ストーリーの進行に伴う変化、追加情報などを記述してください。
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <GenerateButton
        onClick={onGenerate}
        disabled={isGenerating || (updateMode && !updateSource)}
        isLoading={isGenerating}
      />
    </div>
  )
}